import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { TrendingUp, TrendingDown, Minus, AlertCircle } from "lucide-react";
import { cn } from "../../lib/utils";

export interface StatisticalData {
  minGap: number;
  maxGap: number;
  averageGap: number;
  medianGap: number;
  tierWins: number;
  tierName: string;
  tierColor?: string;
  totalSpins: number;
  actualProbability: number; // Calculated from real data
  configuredProbability: number | null; // Admin-configured probability
}

interface StatisticalInsightProps {
  data: StatisticalData;
  isLoading?: boolean;
}

export function StatisticalInsight({ data, isLoading }: StatisticalInsightProps) {
  if (isLoading) {
    return (
      <Card className="bg-gradient-to-r from-card to-secondary/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm uppercase tracking-wider text-primary">
            Statistical Insight
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-32 animate-pulse bg-secondary rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  const actualPercent = (data.actualProbability * 100).toFixed(2);
  const configuredPercent = data.configuredProbability
    ? (data.configuredProbability * 100).toFixed(2)
    : null;

  // Calculate deviation from configured probability
  const deviation = data.configuredProbability
    ? ((data.actualProbability - data.configuredProbability) / data.configuredProbability) * 100
    : null;

  // Expected gap based on configured probability (1 / probability)
  const expectedGap = data.configuredProbability
    ? Math.round(1 / data.configuredProbability)
    : null;

  // Determine if running hot (above expected) or cold (below expected)
  const isRunningHot = deviation !== null && deviation > 10;
  const isRunningCold = deviation !== null && deviation < -10;

  return (
    <Card className="bg-gradient-to-r from-card to-secondary/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm uppercase tracking-wider text-primary">
          Statistical Insight — {data.tierName}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Gap Stats Row */}
        <div className="grid grid-cols-3 gap-4">
          <StatBox
            label="Min Gap"
            value={data.minGap}
            trend="neutral"
          />
          <StatBox
            label="Avg Gap"
            value={Math.round(data.averageGap)}
            expectedValue={expectedGap}
            trend={
              expectedGap
                ? data.averageGap < expectedGap * 0.9
                  ? "up"
                  : data.averageGap > expectedGap * 1.1
                    ? "down"
                    : "neutral"
                : "neutral"
            }
          />
          <StatBox
            label="Max Gap"
            value={data.maxGap}
            trend="neutral"
          />
        </div>

        {/* Probability Comparison */}
        <div className="border-t border-border/50 pt-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Actual (Observed) */}
            <div className="text-center">
              <p className="text-xs text-muted-foreground uppercase mb-1">
                Observed Rate
              </p>
              <p
                className="text-2xl font-bold"
                style={{ color: data.tierColor ?? undefined }}
              >
                {actualPercent}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {data.tierWins.toLocaleString()} / {data.totalSpins.toLocaleString()} spins
              </p>
            </div>

            {/* Configured (Expected) */}
            <div className="text-center">
              <p className="text-xs text-muted-foreground uppercase mb-1">
                Configured Rate
              </p>
              {configuredPercent ? (
                <>
                  <p className="text-2xl font-bold text-muted-foreground">
                    {configuredPercent}%
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Expected 1 in {expectedGap?.toLocaleString()}
                  </p>
                </>
              ) : (
                <p className="text-lg text-muted-foreground/50">Not configured</p>
              )}
            </div>
          </div>

          {/* Deviation indicator */}
          {deviation !== null && (
            <div
              className={cn(
                "mt-4 p-3 rounded-lg flex items-center gap-2",
                isRunningHot && "bg-emerald-500/10 border border-emerald-500/30",
                isRunningCold && "bg-red-500/10 border border-red-500/30",
                !isRunningHot && !isRunningCold && "bg-secondary/50"
              )}
            >
              {isRunningHot ? (
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              ) : isRunningCold ? (
                <TrendingDown className="w-4 h-4 text-red-400" />
              ) : (
                <Minus className="w-4 h-4 text-muted-foreground" />
              )}
              <span
                className={cn(
                  "text-sm",
                  isRunningHot && "text-emerald-400",
                  isRunningCold && "text-red-400",
                  !isRunningHot && !isRunningCold && "text-muted-foreground"
                )}
              >
                {isRunningHot
                  ? `Running hot! +${deviation.toFixed(1)}% above expected rate`
                  : isRunningCold
                    ? `Running cold. ${deviation.toFixed(1)}% below expected rate`
                    : `Within expected range (${deviation > 0 ? "+" : ""}${deviation.toFixed(1)}%)`}
              </span>
            </div>
          )}

          {/* Sample size warning */}
          {data.totalSpins < 100 && (
            <div className="mt-3 flex items-center gap-2 text-amber-400/80 text-xs">
              <AlertCircle className="w-3 h-3" />
              <span>Limited data — statistics may not be reliable yet</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface StatBoxProps {
  label: string;
  value: number;
  expectedValue?: number | null;
  trend?: "up" | "down" | "neutral";
}

function StatBox({ label, value, expectedValue, trend = "neutral" }: StatBoxProps) {
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;

  return (
    <div className="text-center">
      <p className="text-3xl font-bold text-foreground">{value.toLocaleString()}</p>
      <div className="flex items-center justify-center gap-1 mt-1">
        <TrendIcon
          className={cn(
            "h-3 w-3",
            trend === "up" && "text-emerald-400",
            trend === "down" && "text-red-400",
            trend === "neutral" && "text-muted-foreground"
          )}
        />
        <p className="text-xs text-muted-foreground uppercase">{label}</p>
      </div>
      {expectedValue && (
        <p className="text-xs text-muted-foreground/60 mt-0.5">
          (exp: {expectedValue.toLocaleString()})
        </p>
      )}
    </div>
  );
}
